import React, { useState, useEffect } from "react";
import ComponentWrapper from "../components/Shared/ComponentWrapper";

const SECTIONS = [
  { id: "getting-started", label: "Getting Started" },
  { id: "tokens", label: "Helix Tokens & Pricing" },
  { id: "models", label: "AI Models" },
  { id: "skills", label: "Skills" },
  { id: "developer", label: "Developer API" },
  { id: "x402", label: "x402 Payments" },
  { id: "hlx-token", label: "HLX Token" },
  { id: "staking", label: "Staking & Rewards" },
  { id: "governance", label: "Governance" },
  { id: "treasury", label: "Treasury & Vault" },
  { id: "contracts", label: "Contract Addresses" },
  { id: "actions", label: "What You Can Do" },
  { id: "deploy", label: "Deploy Contracts" },
  { id: "swap", label: "Swap Tokens" },
  { id: "wallets", label: "Wallets & Security" },
];

const CONTRACTS = [
  {
    name: "HLX Token",
    address: "0x28D4e499C4CdE621e1Cea7c9CBf9D43bf75a9525",
    role: "ERC-20 governance & utility token",
  },
  {
    name: "Staking (stHLX)",
    address: "0xcfb4853f1d314bb976a575202fc8bdf4fef4b865",
    role: "Stake HLX, earn rewards & voting power",
  },
  {
    name: "Governor",
    address: "0x4546c18b7160a10213890ecd3ea7c039bb10e9c2",
    role: "Proposal creation, voting & execution",
  },
  {
    name: "Timelock",
    address: "0xe2c7fb4063c03e7082922684d7e631c64de82574",
    role: "Enforces delay before execution",
  },
  {
    name: "Treasury / Reward Vault",
    address: "0xf166767f064cabc8ef6548fbbe8f8f1554fa4f9f",
    role: "Holds & distributes HLX rewards",
  },
];

const TOKEN_PRICING = [
  { action: "Chat message", cost: "Metered — model cost × 1.5" },
  { action: "Check balances / portfolio", cost: "Metered (per message)" },
  { action: "Token swap", cost: "Metered (per message)" },
  { action: "Transfer tokens", cost: "Metered (per message)" },
  { action: "Deploy ERC-20 token", cost: "10,000 HT ($0.40)" },
  { action: "Deploy custom Solidity contract", cost: "100,000 HT ($4.00)" },
  { action: "Pay a paywalled API (x402)", cost: "USD spent × 1.5 (paid HT)" },
];

const DocsPage = () => {
  const [activeSection, setActiveSection] = useState("getting-started");

  useEffect(() => {
    const handleScroll = () => {
      const offsets = SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        if (!el) return { id, top: Infinity };
        return { id, top: el.getBoundingClientRect().top };
      });
      const current = offsets.reduce((closest, item) =>
        item.top >= -100 && item.top < closest.top ? item : closest,
        { id: offsets[0].id, top: Infinity }
      );
      setActiveSection(current.id);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="w-full min-h-screen bg-white">
      <ComponentWrapper styles="pt-12 sm:pt-20 pb-20">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-[36px] sm:text-[48px] font-semibold text-blue-1 leading-tight mb-4">
            Documentation
          </h1>
          <p className="text-[18px] text-gray-1 max-w-[600px] leading-[28px]">
            Everything you need to know about using Helix AI — from getting
            started, choosing models like GPT-6 Astra and GPT-6 Astra Pro, and
            building skills to the developer API, x402 payments, staking HLX,
            and on-chain governance.
          </p>
        </div>

        <div className="flex gap-12 relative">
          {/* Sidebar nav — desktop only */}
          <aside className="hidden lg:block w-[220px] flex-shrink-0">
            <nav className="sticky top-[100px]">
              <ul className="flex flex-col gap-1">
                {SECTIONS.map(({ id, label }) => (
                  <li key={id}>
                    <button
                      onClick={() => scrollTo(id)}
                      className={`text-left w-full text-[14px] px-3 py-2 rounded-[8px] transition-all duration-200 ${
                        activeSection === id
                          ? "bg-blue-2/10 text-blue-2 font-semibold"
                          : "text-gray-1 hover:text-blue-1 hover:bg-gray-50"
                      }`}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Getting Started */}
            <Section id="getting-started" title="Getting Started">
              <P>
                Helix AI is an autonomous blockchain agent you interact with
                through natural language. Instead of navigating complex DeFi UIs,
                you simply tell the AI what you want to do and it handles the
                rest.
              </P>
              <H3>1. Connect your wallet</H3>
              <P>
                Visit{" "}
                <A href="https://ai.helix.org">ai.helix.org</A> and sign in
                with any of the supported methods:
              </P>
              <Ul>
                <Li>Google or Apple account</Li>
                <Li>Email or phone number</Li>
                <Li>Passkey (biometric)</Li>
                <Li>External wallet (MetaMask, Coinbase Wallet, etc.)</Li>
              </Ul>
              <P>
                When you connect, a smart account is created for you with
                gas-sponsored transactions on Ethereum — so you don't need ETH
                to get started.
              </P>
              <H3>2. Start chatting</H3>
              <P>
                Once connected, you'll see the chat interface with suggested
                actions like "Check My Balance", "Swap 1 ETH for USDC", or
                "Launch an ERC-20 Token". Type a message or tap a suggestion to
                get started. Every user gets a free daily allowance of Helix
                Tokens to cover their first messages each day.
              </P>
              <H3>3. Pick your AI model</H3>
              <P>
                Helix routes to 300+ AI models. Use the model selector in the
                chat to choose the one that fits your task — from fast, low-cost
                models to frontier models including{" "}
                <strong>GPT-6 Astra</strong> and{" "}
                <strong>GPT-6 Astra Pro</strong>. Your choice is remembered
                across sessions.
              </P>
              <H3>4. Confirm transactions</H3>
              <P>
                Before executing any on-chain action, Helix AI will show you the
                details and ask for confirmation. You always have the final say
                on what gets signed.
              </P>
            </Section>

            <Divider />

            {/* Helix Tokens & Pricing */}
            <Section id="tokens" title="Helix Tokens & Pricing">
              <P>
                Usage on Helix AI is metered in <strong>Helix Tokens (HT)</strong>
                — a prepaid balance you top up with HLX. Instead of a flat
                per-message fee, each chat message burns HT based on the{" "}
                <strong>real AI usage it consumes</strong>, so simple questions
                cost a fraction of what a long, complex task does.
              </P>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <StatCard label="Peg">1 HLX = 1,000 HT</StatCard>
                <StatCard label="Price per Helix Token">$0.00004</StatCard>
                <StatCard label="Free daily allowance">
                  2,500 HT / day
                </StatCard>
                <StatCard label="Max per purchase">10,000,000 HT</StatCard>
              </div>

              <H3>How message billing works</H3>
              <P>
                When you send a message, the AI model reads your prompt (input
                tokens) and writes a reply (output tokens). Helix charges the{" "}
                <strong>provider's real token cost × 1.5</strong>, converted into
                HT. Pricier models and longer replies burn proportionally more.
                Billing happens <strong>after</strong> each message from the
                model's actual usage — you're never charged a flat rate you
                didn't use.
              </P>
              <Code>
{`HT charged = (input_tokens × input_rate
            + output_tokens × output_rate)
            × 1.5 margin ÷ $0.00004 per HT`}
              </Code>

              <H3>Service fees</H3>
              <P>
                On-chain deployments carry a flat HT fee on top of gas, covering
                compilation and deployment infrastructure:
              </P>
              <div className="rounded-[16px] border border-[#F0F0F5] overflow-hidden my-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#FBFAFA]">
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Action
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3 text-right">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {TOKEN_PRICING.map((row, i) => (
                      <tr
                        key={i}
                        className="border-t border-[#F0F0F5]"
                      >
                        <td className="text-[15px] text-gray-700 px-5 py-3">
                          {row.action}
                        </td>
                        <td className="text-[15px] text-blue-1 font-medium px-5 py-3 text-right">
                          {row.cost}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <InfoCard title="Free daily allowance">
                Every user receives <strong>2,500 free Helix Tokens</strong> each
                day — enough for a handful of messages depending on the model you
                pick. Free tokens are always spent before your paid balance, and
                the allowance refreshes daily. No purchase required.
              </InfoCard>

              <H3>Buying Helix Tokens</H3>
              <P>
                Need more? Top up with HLX directly inside the app:
              </P>
              <Ul>
                <Li>
                  <strong>1 HLX = 1,000 Helix Tokens</strong> ($0.04 worth of
                  usage)
                </Li>
                <Li>Up to 10,000,000 HT per purchase</Li>
                <Li>Tokens are credited to your balance instantly</Li>
              </Ul>
            </Section>

            <Divider />

            {/* AI Models */}
            <Section id="models" title="AI Models">
              <P>
                Helix isn't locked to a single model. Through our model router you
                get access to <strong>300+ tool-capable models from 40+
                providers</strong> — all behind one balance and one chat
                interface. The catalog refreshes about every 15 minutes, so new
                models appear in the selector as soon as they're released, with no
                app update required.
              </P>

              <InfoCard title="GPT-6 Astra is live">
                OpenAI's flagship <strong>GPT-6 Astra</strong> and{" "}
                <strong>GPT-6 Astra Pro</strong> are available in the model
                selector now. Astra is built for long-horizon agent work —
                analysis, coding, research, and multi-step on-chain tasks. Astra
                Pro is the same model with higher reasoning effort for the
                hardest jobs. Pick either from the chat header, or pass{" "}
                <Mono>openai/gpt-6-astra</Mono> /{" "}
                <Mono>openai/gpt-6-astra-pro</Mono> to the API.
              </InfoCard>

              <H3>Supported providers</H3>
              <P>
                Models span the full landscape of frontier and open-source labs.
                Some of the providers available today:
              </P>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-6">
                {[
                  ["OpenAI", "GPT-6 Astra, GPT-5.x, GPT-4o, o-series (59)"],
                  ["Qwen", "Qwen series (45)"],
                  ["Google", "Gemini 3.x, Gemma (20)"],
                  ["Mistral AI", "Mistral & Mixtral (17)"],
                  ["Anthropic", "Claude Opus, Sonnet, Haiku (15)"],
                  ["Z.AI", "GLM series (12)"],
                  ["DeepSeek", "DeepSeek V-series (10)"],
                  ["NVIDIA", "Nemotron (9)"],
                  ["Moonshot AI", "Kimi (7)"],
                  ["MiniMax", "MiniMax (6)"],
                  ["Amazon", "Nova (5)"],
                  ["Meta", "Llama (5)"],
                  ["xAI", "Grok (4)"],
                  ["Cohere", "Command (3)"],
                  ["…and 25+ more", "ByteDance, AI21, Reka & others"],
                ].map(([name, models], i) => (
                  <div
                    key={i}
                    className="border border-[#F0F0F5] rounded-[14px] px-4 py-3"
                  >
                    <p className="text-[14px] font-semibold text-blue-1">
                      {name}
                    </p>
                    <p className="text-[12px] text-gray-1 leading-[18px] mt-0.5">
                      {models}
                    </p>
                  </div>
                ))}
              </div>

              <H3>Choosing a model</H3>
              <P>
                Open the model selector in the chat header and search for any
                model by name. Pick a fast, inexpensive model for quick questions,
                or a frontier model for complex reasoning and coding. Every model
                is labeled with its live per-1k-token rate, and your selection is
                saved for every new message until you change it. The default model
                is <Mono>openai/gpt-4o</Mono>. For flagship reasoning, switch to{" "}
                <Mono>openai/gpt-6-astra</Mono> or{" "}
                <Mono>openai/gpt-6-astra-pro</Mono>.
              </P>

              <H3>How models affect cost</H3>
              <P>
                Every model has its own per-token price. Because billing is
                usage-metered (see the Helix Tokens & Pricing section above),
                cheaper models stretch your balance much further, while premium
                models burn more HT per message. To illustrate the range, here are
                example rates (Helix Tokens per 1,000 tokens):
              </P>
              <div className="rounded-[16px] border border-[#F0F0F5] overflow-hidden my-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#FBFAFA]">
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Model
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3 text-right">
                        Input HT / 1k
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3 text-right">
                        Output HT / 1k
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["GPT-4o mini", "~6", "~23"],
                      ["Gemini 3.5 Flash", "~56", "~338"],
                      ["Claude Sonnet 5", "~75", "~375"],
                      ["GPT-4o", "~94", "~375"],
                      ["GPT-5.5", "~188", "~1,125"],
                      ["GPT-6 Astra / Astra Pro", "~375", "~1,875"],
                    ].map(([model, inRate, outRate], i) => (
                      <tr key={i} className="border-t border-[#F0F0F5]">
                        <td className="text-[15px] text-gray-700 px-5 py-3">
                          {model}
                        </td>
                        <td className="text-[15px] text-blue-1 font-medium px-5 py-3 text-right">
                          {inRate}
                        </td>
                        <td className="text-[15px] text-blue-1 font-medium px-5 py-3 text-right">
                          {outRate}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <P className="text-[14px] text-gray-1">
                Rates are illustrative and track each provider's live pricing —
                the exact rate for any model is always shown in the selector and
                the chat displays your running cost so there are no surprises.
              </P>

              <InfoCard title="Only tool-capable models">
                Helix AI is an agent — it needs models that support tool calling
                to check balances, swap, deploy, and more. The selector only lists
                models capable of running the agent's on-chain tools, so every
                model you can pick works with the full feature set.
              </InfoCard>
            </Section>

            <Divider />

            {/* Skills */}
            <Section id="skills" title="Skills">
              <P>
                Skills let you teach Helix AI your own instructions and domain
                knowledge. A skill is a reusable set of guidelines the agent
                automatically follows whenever your request relates to it — think
                of them as custom playbooks for your workflows.
              </P>

              <H3>How skills work</H3>
              <Ol>
                <Li>
                  <strong>Create a skill</strong> with a name, a short
                  description, and markdown instructions (up to 16,000
                  characters).
                </Li>
                <Li>
                  <strong>Enable it.</strong> Enabled skills are surfaced to the
                  agent by name and description on every chat.
                </Li>
                <Li>
                  <strong>The agent loads it automatically.</strong> When your
                  request matches a skill, Helix reads its full instructions
                  before acting — no need to paste the same context every time.
                </Li>
              </Ol>

              <H3>What you can put in a skill</H3>
              <Ul>
                <Li>Step-by-step procedures for tasks you repeat often.</Li>
                <Li>
                  Preferred defaults (slippage, token lists, naming conventions).
                </Li>
                <Li>Domain knowledge about your project, protocol, or team.</Li>
                <Li>Formatting and tone rules for the agent's responses.</Li>
              </Ul>

              <InfoCard title="Skill limits">
                Each account can have up to <strong>20 skills</strong>, with a
                60-character name, a 200-character description, and up to 16,000
                characters of instructions per skill. Toggle any skill on or off
                at any time from the Skills page.
              </InfoCard>
            </Section>

            <Divider />

            {/* Developer API */}
            <Section id="developer" title="Developer API">
              <P>
                Bring the Helix agent into your own apps, scripts, and backends.
                Helix exposes an{" "}
                <strong>OpenAI-compatible API</strong>, so any existing OpenAI SDK
                works by pointing it at{" "}
                <Mono>https://ai.helix.org/api/v1</Mono>. Pay with a Helix API
                key (full agent, billed in HT) or with{" "}
                <strong>x402</strong> (USDC on Base, no key).
              </P>
              <P>
                Full reference, streaming examples, and error codes live in the
                in-app docs at{" "}
                <A href="https://ai.helix.org/developer/docs">
                  ai.helix.org/developer/docs
                </A>
                .
              </P>

              <H3>Two ways to pay</H3>
              <Ul>
                <Li>
                  <strong>API key</strong> (<Mono>hlx_sk_...</Mono>) — the same
                  system prompt, tools, skills, and RAG as web chat, including
                  on-chain transfers, swaps, and deploys. Usage bills the key
                  owner's Helix Token balance.
                </Li>
                <Li>
                  <strong>x402</strong> — send no key. The server returns HTTP
                  402 with a <strong>$0.05 USDC</strong> requirement on Base.
                  After payment, you get a read-only guest agent (prices,
                  wallet reads, swap quotes, Helix docs). No transfers, swaps,
                  deploys, skills, or outbound x402.
                </Li>
              </Ul>
              <P>
                If a valid Helix key is sent, HT billing is used and x402 is
                ignored so you are not charged twice.
              </P>

              <H3>1. Create an API key</H3>
              <P>
                Head to the <strong>Developer</strong> page in the app and create
                a key. Keys start with <Mono>hlx_sk_</Mono> and are shown{" "}
                <strong>only once</strong> at creation — copy it somewhere safe.
                Helix stores only a hashed version, never the plaintext.
              </P>
              <Ul>
                <Li>Up to 10 active keys per account.</Li>
                <Li>Revoke any key instantly (soft-disabled server-side).</Li>
                <Li>
                  Usage bills the key owner's Helix Token balance, just like web
                  chat.
                </Li>
              </Ul>

              <H3>2. Point your client at Helix</H3>
              <P>
                Set the base URL to{" "}
                <Mono>https://ai.helix.org/api/v1</Mono> and authenticate with
                your key as a bearer token.
              </P>
              <Code>
{`import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.HELIX_API_KEY, // hlx_sk_...
  baseURL: "https://ai.helix.org/api/v1",
});

const res = await client.chat.completions.create({
  model: "openai/gpt-4o",
  messages: [{ role: "user", content: "What's my wallet balance?" }],
});`}
              </Code>

              <H3>Endpoints</H3>
              <div className="rounded-[16px] border border-[#F0F0F5] overflow-hidden my-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#FBFAFA]">
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Endpoint
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["POST /chat/completions", "Run the agent. API key = full tools + HT billing. No key = x402 USDC guest (reads only). Streaming and non-streaming."],
                      ["GET /models", "List available AI models (OpenAI list shape). Public — no key required."],
                      ["GET /credits", "Check your free, paid, and total Helix Token balance. API key only."],
                    ].map(([ep, desc], i) => (
                      <tr key={i} className="border-t border-[#F0F0F5] align-top">
                        <td className="px-5 py-3">
                          <code className="text-[13px] text-blue-2 whitespace-nowrap">
                            {ep}
                          </code>
                        </td>
                        <td className="text-[14px] text-gray-700 px-5 py-3">
                          {desc}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <P>
                The <Mono>/chat/completions</Mono> endpoint with an API key runs
                the complete Helix agent — including on-chain actions — so API
                calls can check balances, swap, transfer, and deploy exactly like
                the web app. Requests are stateless and are never saved to your
                chat history. Model ids are OpenRouter slugs, for example{" "}
                <Mono>openai/gpt-4o</Mono> or{" "}
                <Mono>openai/gpt-6-astra</Mono>.
              </P>

              <H3>3. Or pay with x402 (no key)</H3>
              <P>
                Omit <Mono>Authorization</Mono>. Helix responds with HTTP 402 and
                USDC payment requirements on Base. Any x402-compatible wallet can
                sign and retry; with thirdweb, wrap <Mono>fetch</Mono> so the 402
                is paid automatically:
              </P>
              <Code>
{`import { wrapFetchWithPayment } from "thirdweb/x402";
import { createThirdwebClient } from "thirdweb";
import { createWallet } from "thirdweb/wallets";

const client = createThirdwebClient({
  clientId: process.env.THIRDWEB_CLIENT_ID,
});
const wallet = createWallet("io.metamask");
await wallet.connect({ client });

const fetchWithPay = wrapFetchWithPayment(
  fetch, client, wallet, 100_000n
);

const res = await fetchWithPay(
  "https://ai.helix.org/api/v1/chat/completions",
  {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "openai/gpt-6-astra",
      messages: [{ role: "user", content: "What is HLX?" }],
    }),
  }
);`}
              </Code>

              <InfoCard title="Treat your key like a wallet key">
                Because API calls inherit your account's balance and agent wallet,
                a leaked key can spend your Helix Tokens and move funds from your
                agent wallet. Keys are hashed at rest and can be revoked
                instantly. Never commit a key to source control or expose it
                client-side.
              </InfoCard>
            </Section>

            <Divider />

            {/* x402 Payments */}
            <Section id="x402" title="x402 Payments">
              <P>
                Helix uses the{" "}
                <A href="https://x402.org">x402</A> protocol (HTTP 402
                Payment Required) in two directions. They are not the same
                thing.
              </P>

              <H3>The agent pays for paywalled APIs</H3>
              <P>
                Ask Helix to fetch a URL. If the endpoint returns HTTP 402, the
                agent wallet pays in USDC — on Base by default, or on whatever
                network the endpoint requires — and you are billed the USD spent
                × 1.5 from your <strong>paid</strong> Helix Token balance. Free
                URLs are fetched at no extra cost.
              </P>
              <Ul>
                <Li>
                  Capped at <strong>$1.00 per request</strong>, or whatever your
                  paid HT balance can cover, whichever is lower.
                </Li>
                <Li>
                  This never touches your own ETH/ERC-20 balances, swaps, or
                  deploys — those stay on Ethereum mainnet.
                </Li>
                <Li>
                  Outbound x402 is available in web chat and keyed API calls,
                  not on x402-paid (guest) API requests.
                </Li>
              </Ul>
              <Code>
                {`"Fetch this paywalled API and summarize the response: https://…"`}
              </Code>

              <H3>Pay Helix's API with USDC</H3>
              <P>
                Call{" "}
                <Mono>POST https://ai.helix.org/api/v1/chat/completions</Mono>{" "}
                without a Helix key. Unpaid requests return HTTP 402. Pay{" "}
                <strong>$0.05 USDC on Base</strong> per completion and get a
                read-only guest agent — prices, wallet reads, swap quotes, and
                Helix protocol answers. Settlement happens after a successful
                reply, so a failed request is not charged.
              </P>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <StatCard label="Price">$0.05 USDC / completion</StatCard>
                <StatCard label="Network">Base (chain ID 8453)</StatCard>
                <StatCard label="Asset">USDC, paid to Helix</StatCard>
                <StatCard label="Guest tools">
                  Reads, quotes, RAG — no writes
                </StatCard>
              </div>
              <InfoCard title="Key vs x402">
                An API key runs the full agent (including moving funds from your
                agent wallet). x402 is a per-request USDC payment with a
                read-only guest. If both are sent, the key wins and you are not
                charged USDC.
              </InfoCard>
            </Section>

            <Divider />

            {/* HLX Token */}
            <Section id="hlx-token" title="HLX Token">
              <P>
                HLX is the utility token that powers the Helix AI ecosystem. Use
                it to top up your Helix Token balance, participate in governance,
                and shape the future of the platform.
              </P>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <StatCard label="Contract Address">
                  <code className="text-[13px] text-blue-2 break-all">
                    0x28d4e499c4cde621e1cea7c9cbf9d43bf75a9525
                  </code>
                </StatCard>
                <StatCard label="Network">Ethereum (Chain ID 1)</StatCard>
                <StatCard label="Total Supply">100,000,000,000 HLX</StatCard>
                <StatCard label="Decimals">18</StatCard>
              </div>
              <H3>How to get HLX</H3>
              <Ol>
                <Li>
                  Swap for HLX on a DEX like{" "}
                  <A href="https://app.uniswap.org">Uniswap</A> on the Ethereum
                  network.
                </Li>
                <Li>
                  Make sure you're on the <strong>Ethereum network</strong> and
                  verify the contract address above before swapping.
                </Li>
                <Li>
                  Once you have HLX in your wallet, you can use it to top up your
                  Helix Token balance inside Helix AI.
                </Li>
              </Ol>
              <InfoCard title="Add HLX to your wallet">
                You can add the HLX token to MetaMask or any EVM wallet using
                the contract address above on the Ethereum network.
              </InfoCard>
            </Section>

            <Divider />

            {/* Staking & Rewards */}
            <Section id="staking" title="Staking & Rewards">
              <P>
                Staking is the heart of the Helix ecosystem. When you stake HLX,
                you receive non-transferable <strong>staked Helix Token
                (stHLX)</strong> voting units, earn ongoing rewards, and unlock
                the right to participate in governance. The staking contract is
                deployed at{" "}
                <Mono>0xcfb4853f1d314bb976a575202fc8bdf4fef4b865</Mono>.
              </P>

              <H3>How staking works</H3>
              <Ol>
                <Li>
                  <strong>Stake:</strong> Approve and deposit HLX into the
                  staking contract. Your staked balance is tracked on-chain and
                  immediately begins earning rewards.
                </Li>
                <Li>
                  <strong>Earn:</strong> Rewards accrue continuously based on a
                  configurable APR. The longer you stake, the more you earn —
                  rewards compound in real time and can be claimed at any moment.
                </Li>
                <Li>
                  <strong>Vote:</strong> Staking automatically grants you voting
                  power equal to your staked balance, and self-delegates it to
                  you on your first stake so you're ready to govern.
                </Li>
                <Li>
                  <strong>Withdraw or exit:</strong> Unstake any amount at any
                  time, or call <Mono>exit()</Mono> to withdraw your entire
                  stake and claim all pending rewards in a single transaction.
                </Li>
              </Ol>

              <H3>Reward mechanics</H3>
              <P>
                Rewards are calculated using a continuous{" "}
                <em>reward-per-token</em> accumulator. The rate is expressed in
                basis points (APR), where 10,000 bps = 100% APR — the protocol's
                maximum. Each time you interact with the contract, your earned
                rewards are checkpointed so no rewards are ever lost.
              </P>
              <Code>
{`rewardIncrement =
  (timeElapsed × aprBps × 1e18) / 10,000 / 365 days

earned(user) =
  stakedBalance × (rewardPerToken − userPaid) / 1e18
  + accruedRewards`}
              </Code>
              <P>
                Reward payouts are pulled from the Reward Vault — a separate,
                pre-funded contract — so claiming rewards never dilutes other
                stakers' principal. Only the staking contract is authorized to
                request payouts from the vault.
              </P>

              <H3>Key staking actions</H3>
              <div className="rounded-[16px] border border-[#F0F0F5] overflow-hidden my-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#FBFAFA]">
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Function
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        What it does
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["stake(amount)", "Deposit HLX, start earning, gain voting power"],
                      ["withdraw(amount)", "Unstake a portion of your HLX"],
                      ["claimReward()", "Claim accrued HLX rewards from the vault"],
                      ["exit()", "Withdraw everything and claim all rewards"],
                      ["earned(account)", "View pending rewards for an address"],
                    ].map(([fn, desc], i) => (
                      <tr key={i} className="border-t border-[#F0F0F5]">
                        <td className="px-5 py-3">
                          <code className="text-[13px] text-blue-2">{fn}</code>
                        </td>
                        <td className="text-[14px] text-gray-700 px-5 py-3">
                          {desc}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <InfoCard title="APR is governance-controlled">
                The staking APR isn't set by a central admin — it can only be
                changed through a successful governance proposal that executes
                via the Timelock. Stakers ultimately control their own reward
                rate.
              </InfoCard>
            </Section>

            <Divider />

            {/* Governance */}
            <Section id="governance" title="Governance">
              <P>
                Helix is governed entirely on-chain by HLX stakers. The system is
                built on the battle-tested OpenZeppelin Governor framework and
                consists of two contracts working together: the{" "}
                <strong>Governor</strong>{" "}
                (<Mono>0x4546c18b7160a10213890ecd3ea7c039bb10e9c2</Mono>) and the{" "}
                <strong>Timelock</strong>{" "}
                (<Mono>0xe2c7fb4063c03e7082922684d7e631c64de82574</Mono>).
                Create, vote, queue, and execute proposals in the app at{" "}
                <A href="https://ai.helix.org/governance">
                  ai.helix.org/governance
                </A>
                .
              </P>

              <H3>Where voting power comes from</H3>
              <P>
                Voting power is derived directly from staked HLX. Your stHLX
                balance equals your votes — there's no separate wrapping step.
                This means only users with skin in the game (actively staked
                HLX) can influence protocol decisions, aligning voters with the
                long-term health of the ecosystem.
              </P>

              <H3>The governance lifecycle</H3>
              <Ol>
                <Li>
                  <strong>Propose:</strong> Any address holding at least the
                  proposal threshold of voting power can submit a proposal — a
                  set of on-chain actions (e.g. "change staking APR to 8%" or
                  "grant treasury HLX to a grant recipient"). In the app this is
                  a three-step flow: details → actions → review.
                </Li>
                <Li>
                  <strong>Voting delay:</strong> After submission, a short delay
                  passes before voting opens, giving the community time to review.
                </Li>
                <Li>
                  <strong>Vote:</strong> During the voting period, stakers cast{" "}
                  <em>For</em>, <em>Against</em>, or <em>Abstain</em> votes. A
                  proposal passes if it reaches quorum and has more For than
                  Against votes.
                </Li>
                <Li>
                  <strong>Queue:</strong> A passed proposal is queued in the
                  Timelock, where it must wait out a mandatory minimum delay.
                </Li>
                <Li>
                  <strong>Execute:</strong> After the delay, anyone can execute
                  the proposal. The Timelock — not the Governor — carries out the
                  actual on-chain changes.
                </Li>
              </Ol>

              <H3>Governance parameters</H3>
              <div className="rounded-[16px] border border-[#F0F0F5] overflow-hidden my-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#FBFAFA]">
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Parameter
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Value
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3 hidden sm:table-cell">
                        Purpose
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Voting Delay", "~1 hour", "Time between proposal creation and when voting opens"],
                      ["Voting Period", "~2 days", "How long votes can be cast on a proposal"],
                      ["Proposal Threshold", "1B stHLX (~1% of supply)", "Minimum voting power required to create a proposal"],
                      ["Quorum", "2B stHLX", "Minimum total votes needed for a proposal to be valid"],
                      ["Timelock Delay", "On-chain delay", "Mandatory wait before a passed proposal executes"],
                    ].map(([param, value, desc], i) => (
                      <tr key={i} className="border-t border-[#F0F0F5]">
                        <td className="px-5 py-3 text-[14px] font-medium text-blue-1">
                          {param}
                        </td>
                        <td className="px-5 py-3 text-[14px] font-medium text-blue-1 whitespace-nowrap">
                          {value}
                        </td>
                        <td className="text-[14px] text-gray-700 px-5 py-3 hidden sm:table-cell">
                          {desc}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <P>
                These parameters are themselves upgradeable through governance —
                for example, the community can vote to adjust the quorum or
                voting period as the ecosystem matures. Creating a proposal
                requires voting power at or above the threshold. Succeeded
                proposals show a Queue button; Queued proposals show Execute
                once the timelock delay has passed.
              </P>

              <InfoCard title="Why a Timelock?">
                The Timelock guarantees that no approved change takes effect
                instantly. This delay gives every user a window to review pending
                changes and, if necessary, exit their position before a proposal
                executes — a critical safety guarantee in decentralized
                governance.
              </InfoCard>
            </Section>

            <Divider />

            {/* Treasury & Vault */}
            <Section id="treasury" title="Treasury & Vault">
              <P>
                The Reward Vault{" "}
                (<Mono>0xf166767f064cabc8ef6548fbbe8f8f1554fa4f9f</Mono>) serves a
                dual role as both the <strong>staking reward pool</strong> and the{" "}
                <strong>protocol treasury</strong>. It holds HLX that is used to
                pay staking rewards and to fund ecosystem initiatives approved by
                governance.
              </P>

              <H3>What the vault does</H3>
              <Ul>
                <Li>
                  <strong>Funds rewards:</strong> Anyone can contribute HLX to
                  the vault via <Mono>fundRewards()</Mono>, topping up the pool
                  that pays stakers.
                </Li>
                <Li>
                  <strong>Pays stakers:</strong> Only the staking contract can
                  request reward payouts, ensuring funds are released exactly as
                  the staking math dictates.
                </Li>
                <Li>
                  <strong>Treasury grants:</strong> Through{" "}
                  <Mono>grantHLX()</Mono>, the vault owner (the Timelock) can
                  disburse HLX to recipients — but only as the result of a
                  successful governance proposal.
                </Li>
              </Ul>

              <H3>Ownership & control</H3>
              <P>
                The vault is owned by the Timelock, which means every treasury
                action — funding grants, redirecting rewards, or pointing to a
                new staking contract — must pass through the full governance
                process. No individual can unilaterally move treasury funds.
                Reward payouts are protected by reentrancy guards and strict
                access control so only the authorized staking contract can
                trigger them.
              </P>

              <InfoCard title="Separation of funds">
                Keeping rewards in a dedicated vault — separate from the staking
                contract that holds principal — means claiming rewards can never
                touch another user's staked HLX. Principal and rewards are
                always isolated.
              </InfoCard>
            </Section>

            <Divider />

            {/* Contract Addresses */}
            <Section id="contracts" title="Contract Addresses">
              <P>
                All Helix protocol contracts are deployed on Ethereum mainnet.
                Always verify these addresses before interacting with any
                contract.
              </P>
              <div className="rounded-[16px] border border-[#F0F0F5] overflow-hidden my-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#FBFAFA]">
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Contract
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3 hidden sm:table-cell">
                        Role
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Address
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {CONTRACTS.map((c, i) => (
                      <tr key={i} className="border-t border-[#F0F0F5] align-top">
                        <td className="px-5 py-3 text-[14px] font-semibold text-blue-1 whitespace-nowrap">
                          {c.name}
                        </td>
                        <td className="px-5 py-3 text-[13px] text-gray-600 hidden sm:table-cell">
                          {c.role}
                        </td>
                        <td className="px-5 py-3">
                          <a
                            href={`https://etherscan.io/address/${c.address}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[12px] text-blue-2 hover:underline font-mono break-all"
                          >
                            {c.address}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <InfoCard title="How it all connects">
                Stake HLX in the <strong>Staking</strong> contract to earn
                rewards (paid by the <strong>Vault</strong>) and gain voting
                power. Use that power in the <strong>Governor</strong> to pass
                proposals, which execute through the <strong>Timelock</strong> —
                which in turn owns the Staking and Vault contracts. It's a fully
                self-governing loop.
              </InfoCard>
            </Section>

            <Divider />

            {/* What You Can Do */}
            <Section id="actions" title="What You Can Do">
              <P>
                Helix AI comes with 20+ blockchain tools accessible through
                natural language. Here's what you can ask it to do:
              </P>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <ActionCard
                  title="Check Balances"
                  description="View native tokens, ERC-20 holdings, and NFTs on Ethereum."
                  example="What's my wallet balance?"
                />
                <ActionCard
                  title="Swap Tokens"
                  description="Exchange tokens using DEX aggregation for the best rates with 1% default slippage protection."
                  example="Swap 1 ETH for USDC"
                />
                <ActionCard
                  title="Deploy ERC-20 Tokens"
                  description="Launch a new token with a name, symbol, decimals, and total supply — in one message."
                  example="Launch a token called MyCoin with symbol MC and 1M supply"
                />
                <ActionCard
                  title="Deploy Custom Contracts"
                  description="Write and deploy arbitrary Solidity contracts with OpenZeppelin import support."
                  example="Deploy a contract that implements ERC-721"
                />
                <ActionCard
                  title="Transfer Tokens"
                  description="Send native tokens or ERC-20 tokens to any address."
                  example="Send 50 USDC to 0xabc..."
                />
                <ActionCard
                  title="Price Lookups"
                  description="Get real-time prices, trending coins, and token info from CoinGecko."
                  example="What's the price of ETH right now?"
                />
                <ActionCard
                  title="Paywalled APIs (x402)"
                  description="Fetch URLs that charge via HTTP 402. The agent pays in USDC and bills your paid HT — capped at $1 per request."
                  example="Fetch this paywalled API and summarize it"
                />
                <ActionCard
                  title="Blockchain Knowledge"
                  description="Ask about Helix, HLX, DeFi concepts, or any blockchain topic."
                  example="How does staking work?"
                />
              </div>
            </Section>

            <Divider />

            {/* Deploy Contracts */}
            <Section id="deploy" title="Deploy Contracts">
              <P>
                Helix AI can compile and deploy Solidity smart contracts directly
                from your conversation. Contracts are deployed from your wallet
                and verified on the block explorer automatically.
              </P>
              <H3>ERC-20 tokens — 10,000 HT ($0.40)</H3>
              <P>
                The fastest way to launch a token. Just tell the AI the name,
                symbol, decimals, and total supply. It uses a battle-tested
                template and deploys instantly.
              </P>
              <Code>
                {`"Launch a token called Helix Gold with symbol HGOLD, 18 decimals, and 10 million total supply"`}
              </Code>
              <H3>Custom Solidity contracts — 100,000 HT ($4.00)</H3>
              <P>
                For more complex use cases, describe the contract you need or
                paste Solidity code directly. Helix AI supports OpenZeppelin
                imports so you can leverage standard libraries.
              </P>
              <Code>
                {`"Deploy an ERC-721 NFT contract with a mint function that costs 0.01 ETH per mint, max supply of 10,000"`}
              </Code>
              <InfoCard title="Technical details">
                Contracts are compiled with Solidity v0.8.29, optimizer enabled
                (200 runs). OpenZeppelin imports are automatically resolved.
                After deployment, Helix AI attempts to verify the contract on the
                chain's block explorer.
              </InfoCard>
            </Section>

            <Divider />

            {/* Swap Tokens */}
            <Section id="swap" title="Swap Tokens">
              <P>
                Helix AI uses the WowMax DEX aggregator to find optimal swap
                routes across decentralized exchanges. Just tell it what you want
                to swap.
              </P>
              <H3>How it works</H3>
              <Ol>
                <Li>Tell the AI what you want to swap (e.g. "Swap 1 ETH for USDC").</Li>
                <Li>It fetches a quote with the best available rate.</Li>
                <Li>Review the details — amount, rate, and estimated output.</Li>
                <Li>Confirm to execute the transaction.</Li>
              </Ol>
              <InfoCard title="Slippage protection">
                All swaps use a default 1% slippage tolerance to protect you from
                price movements during execution.
              </InfoCard>
            </Section>

            <Divider />

            {/* Wallets & Security */}
            <Section id="wallets" title="Wallets & Security">
              <P>
                Helix AI is fully non-custodial. You connect your own wallet and
                retain complete control of your keys and assets at all times.
              </P>
              <H3>How wallet signing works</H3>
              <P>
                When you connect, Helix AI creates a session key that allows it
                to propose transactions on your behalf. Every transaction still
                requires your explicit confirmation before it's executed. The AI
                never has direct access to your private keys.
              </P>
              <H3>Gas sponsorship</H3>
              <P>
                Transactions on Ethereum are gas-sponsored through account
                abstraction, meaning you don't need ETH in your wallet for gas
                fees.
              </P>
              <H3>Best practices</H3>
              <Ul>
                <Li>Always review transaction details before confirming.</Li>
                <Li>
                  Verify contract addresses when swapping or transferring tokens.
                </Li>
                <Li>
                  Start with small amounts when trying new actions for the first
                  time.
                </Li>
                <Li>
                  Never share your private keys or seed phrase with anyone,
                  including AI assistants.
                </Li>
              </Ul>
            </Section>

            {/* CTA */}
            <div className="mt-16 bg-blue-2 rounded-[24px] p-8 sm:p-12 text-center">
              <h2 className="text-[28px] sm:text-[36px] text-white font-semibold mb-4">
                Ready to get started?
              </h2>
              <p className="text-white/80 text-[16px] sm:text-[18px] max-w-[460px] mx-auto mb-6">
                Launch Helix AI and start interacting with the blockchain
                through natural language.
              </p>
              <a
                href="https://ai.helix.org"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center text-[14px] font-semibold text-blue-1 h-[46px] rounded-[12px] bg-white px-8
                         transition-all duration-200 ease-in-out
                         shadow-md hover:shadow-lg active:shadow-sm
                         transform hover:scale-105 active:scale-95"
              >
                Launch Helix AI
              </a>
            </div>
          </main>
        </div>
      </ComponentWrapper>
    </div>
  );
};

export default DocsPage;

/* ── Reusable sub-components ── */

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-[100px] mb-4">
      <h2 className="text-[28px] sm:text-[32px] font-semibold text-blue-1 mb-5 leading-tight">
        {title}
      </h2>
      {children}
    </section>
  );
}

function H3({ children }) {
  return (
    <h3 className="text-[20px] font-semibold text-blue-1 mt-7 mb-3">
      {children}
    </h3>
  );
}

function P({ children, className = "" }) {
  return (
    <p
      className={`text-[16px] text-gray-600 leading-[26px] mb-3 ${className}`}
    >
      {children}
    </p>
  );
}

function A({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-2 hover:underline font-medium"
    >
      {children}
    </a>
  );
}

function Ul({ children }) {
  return <ul className="list-disc list-inside space-y-2 mb-4 pl-1">{children}</ul>;
}

function Ol({ children }) {
  return (
    <ol className="list-decimal list-inside space-y-2 mb-4 pl-1">{children}</ol>
  );
}

function Li({ children }) {
  return <li className="text-[15px] text-gray-600 leading-[24px]">{children}</li>;
}

function Code({ children }) {
  return (
    <div className="bg-[#1F1F2E] rounded-[12px] px-5 py-4 my-4 overflow-x-auto">
      <code className="text-[14px] text-green-300 font-mono whitespace-pre-wrap">
        {children}
      </code>
    </div>
  );
}

function Mono({ children }) {
  return (
    <code className="text-[13px] text-blue-2 font-mono break-all bg-blue-2/5 px-1.5 py-0.5 rounded">
      {children}
    </code>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-[16px] p-5 my-5">
      <p className="text-[14px] font-semibold text-blue-900 mb-1">{title}</p>
      <p className="text-[14px] text-blue-800 leading-[22px]">{children}</p>
    </div>
  );
}

function StatCard({ label, children }) {
  return (
    <div className="border border-[#F0F0F5] rounded-[16px] p-5">
      <p className="text-[13px] text-gray-1 font-medium mb-2">{label}</p>
      <div className="text-[16px] text-blue-1 font-medium">{children}</div>
    </div>
  );
}

function ActionCard({ title, description, example }) {
  return (
    <div className="border border-[#F0F0F5] rounded-[16px] p-5 flex flex-col gap-2">
      <p className="text-[18px] text-blue-1 font-semibold">{title}</p>
      <p className="text-[14px] text-gray-600 leading-[22px]">{description}</p>
      <div className="mt-auto pt-2">
        <p className="text-[13px] text-gray-400 font-medium">Try:</p>
        <p className="text-[13px] text-blue-2 font-medium italic">
          "{example}"
        </p>
      </div>
    </div>
  );
}

function Divider() {
  return <hr className="border-[#F0F0F5] my-10" />;
}
